import Button from "@/components/Button";
import ProgressBar from "@/components/ProgressBar";
import { Tag } from "@/components/Tag";
import { PlayerRolePositionFocus } from "@/components/features/SquadBuilder/components/PlayerRole/PlayerRolePositionFocus/PlayerRolePositionFocus";
import { TacticFormation } from "@/components/features/Tactics/TacticFormation";
import { IconLink } from "@/components/features/Tactics/TacticInfo";
import { TacticPopularityScorePopup } from "@/components/features/Tactics/TacticPopularityScorePopup";
import Accordion from "@/containers/Accordion";
import {
  InfoIcon,
  copyIcon,
  favorite,
  fbIcon,
  instagramIcon,
  twitchIcon,
  xIcon,
  ytIcon,
} from "@/designSystemIcons";
import {
  positionToChain,
  uniquePositionScores,
} from "@/functions/getFormation";
import getFormation from "@/functions/getFormation";
import { cn } from "@/lib/utils";
import { getFormationsLabel } from "@/redux/loadedContent/loadedContentSlice";
import { Tactic } from "@/redux/tactics/tacticsSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import TacticImage from "../TacticImage";

type PlayerRolePositionFocusComb = {
  id: number;
  focus: string;
  position: string;
  uniquePosition: string;
  playerRoleName?: string;
  playerRoleShortName?: string;
};

interface PlayerRolesSectionProps {
  formationId: string;
  playerRoles: {
    playerRoleId: number;
    focus: string;
  }[];
}
const PlayerRolesSection = ({
  formationId,
  playerRoles,
}: PlayerRolesSectionProps) => {
  const [keeper, setKeeper] = useState<PlayerRolePositionFocusComb>();
  const [defenders, setDefenders] = useState<PlayerRolePositionFocusComb[]>([]);
  const [midfielders, setMidfielders] = useState<PlayerRolePositionFocusComb[]>(
    [],
  );
  const [attackers, setAttackers] = useState<PlayerRolePositionFocusComb[]>([]);

  useEffect(() => {
    let localDefenders: PlayerRolePositionFocusComb[] = [];
    let localMidfielders: PlayerRolePositionFocusComb[] = [];
    let localAttackers: PlayerRolePositionFocusComb[] = [];
    const formation = getFormation(formationId);

    formation.labels.forEach((pos: string, index: number) => {
      switch (positionToChain[pos]) {
        case "goalkeepers":
          setKeeper({
            id: playerRoles[index].playerRoleId,
            focus: playerRoles[index].focus,
            position: pos,
            uniquePosition: formation.uniquePositions[index],
          });
          break;
        case "defenders":
          localDefenders.push({
            id: playerRoles[index].playerRoleId,
            focus: playerRoles[index].focus,
            position: pos,
            uniquePosition: formation.uniquePositions[index],
          });
          break;
        case "midfielders":
          localMidfielders.push({
            id: playerRoles[index].playerRoleId,
            focus: playerRoles[index].focus,
            position: pos,
            uniquePosition: formation.uniquePositions[index],
          });
          break;
        case "attackers":
          localAttackers.push({
            id: playerRoles[index].playerRoleId,
            focus: playerRoles[index].focus,
            position: pos,
            uniquePosition: formation.uniquePositions[index],
          });
          break;
      }
    });

    setDefenders(
      localDefenders.sort(
        (a, b) =>
          uniquePositionScores[b.uniquePosition] -
          uniquePositionScores[a.uniquePosition],
      ),
    );
    setMidfielders(
      localMidfielders.sort(
        (a, b) =>
          uniquePositionScores[b.uniquePosition] -
          uniquePositionScores[a.uniquePosition],
      ),
    );
    setAttackers(
      localAttackers.sort(
        (a, b) =>
          uniquePositionScores[b.uniquePosition] -
          uniquePositionScores[a.uniquePosition],
      ),
    );
  }, [formationId, playerRoles]);

  return (
    <Accordion label="Player Roles" defaultOpen={true}>
      <section>
        <div className="flex flex-row justify-center gap-x-0.5 sb-v2-lg:gap-x-1">
          {attackers.map((attacker, index) => (
            <PlayerRolePositionFocus
              key={index}
              playerRoleId={attacker.id}
              position={attacker.position}
              focus={attacker.focus}
              columns={attackers.length}
            />
          ))}
        </div>
        <div className="flex flex-row justify-center mt-2 gap-x-0.5 sb-v2-lg:gap-x-1">
          {midfielders.map((midfielder, index) => (
            <PlayerRolePositionFocus
              key={index + attackers.length}
              playerRoleId={midfielder.id}
              position={midfielder.position}
              focus={midfielder.focus}
              columns={midfielders.length}
            />
          ))}
        </div>
        <div className="flex flex-row justify-center mt-2 gap-x-1">
          {defenders.map((defender, index) => (
            <PlayerRolePositionFocus
              key={index + attackers.length + midfielders.length}
              playerRoleId={defender.id}
              position={defender.position}
              focus={defender.focus}
              columns={defenders.length}
            />
          ))}
        </div>
        <div className="flex flex-row justify-center mt-2">
          <PlayerRolePositionFocus
            key={10}
            playerRoleId={keeper?.id}
            position={keeper?.position}
            focus={keeper?.focus}
            columns={1}
          />
        </div>
      </section>
    </Accordion>
  );
};

interface TacticMetaInformationSectionProps {
  tactic: Tactic;
  isFavorite: boolean;
  handleCopy: (eaCode: string) => void;
  handleClickFavorite: () => void;
}

const TacticMetaInformationSection = ({
  tactic,
  handleCopy,
  isFavorite,
  handleClickFavorite,
}: TacticMetaInformationSectionProps) => {
  const [showPopularityScorePopup, setShowPopularityScorePopup] =
    useState(false);
  return (
    <section className="flex flex-col w-full">
      <section className="flex flex-row gap-x-1 bg-white/10">
        <TacticImage
          imageUrl={tactic.logoUrl}
          author={tactic.author}
          isTrending={false}
          noBorder={true}
          selectable={false}
          hideAuthors={true}
        />
        <div className="flex flex-col grow gap-y-1 pl-1 py-2 pr-4">
          <div className="flex flex-row justify-between">
            <h2 className="font-medium desktop:font-normal text-sm text-dark-secondary">
              {tactic.name}
            </h2>
            <button
              className={cn(
                "text-dark-tertiary desktop:hover:text-dark-secondary px-2 -m-2 mobile:px-0 mobile:-m-0",
                isFavorite ? "text-white" : "text-dark-tertiary",
              )}
              onClick={(e) => {
                handleClickFavorite();
                e.stopPropagation();
              }}
            >
              <div className="w-5 h-5">{favorite}</div>
            </button>
          </div>
          {tactic.tags.map((tag) => (
            <Tag tag={tag} key={tag} large={true} />
          ))}
          <div className="flex flex-row justify-between mt-auto items-center">
            {tactic.author && (
              <p className="text-dark-tertiary desktop:hidden text-sm">
                {tactic.author}
              </p>
            )}
            {tactic.socials && (
              <div className="-mt-0.5 flex">
                {tactic.socials.ytLink && (
                  <IconLink icon={ytIcon} link={tactic.socials.ytLink} />
                )}
                {tactic.socials.xLink && (
                  <IconLink icon={xIcon} link={tactic.socials.xLink} />
                )}
                {tactic.socials.twitchLink && (
                  <IconLink
                    icon={twitchIcon}
                    link={tactic.socials.twitchLink}
                  />
                )}
                {tactic.socials.instagramLink && (
                  <IconLink
                    icon={instagramIcon}
                    link={tactic.socials.instagramLink}
                  />
                )}
                {tactic.socials.fbLink && (
                  <IconLink icon={fbIcon} link={tactic.socials.fbLink} />
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="flex flex-col w-full gap-y-4 text-sm desktop:text-xs mt-2">
        <section className="flex flex-col px-4 gap-y-3 my-2">
          <div className="flex flex-row text-dark-secondary gap-x-2 items-center">
            <p className="whitespace-nowrap">Popularity Score</p>
            <ProgressBar
              value={tactic.popularityScore}
              totalValue={10}
              small={true}
            />
            <p className="text-sm text-white">{tactic.popularityScore}</p>
            <button
              className="w-9 h-9 desktop:w-5 desktop:h-5 shrink-0 ml-2 p-2 -m-2 mobile:p-0 mobile:-m-0"
              onClick={(e) => {
                setShowPopularityScorePopup(true);
                e.stopPropagation();
              }}
            >
              <InfoIcon outerFill="var(--color-dark-tertiary)" />
            </button>
          </div>
          <p className="text-dark-tertiary">{tactic.description}</p>
          <span className="ml-auto">
            <Button
              variant="tertiary"
              label={tactic.eaCode}
              onClick={(e) => {
                handleCopy(tactic.eaCode);
                e.stopPropagation();
              }}
              icon={<span className="fill-dark-secondary">{copyIcon}</span>}
            />
          </span>
        </section>
      </div>
      {showPopularityScorePopup && (
        <TacticPopularityScorePopup
          onClose={() => setShowPopularityScorePopup(false)}
        />
      )}
    </section>
  );
};

const TacticalPresetSection = ({
  formationId,
  buildUpStyle,
  defensiveApproach,
  lineHeight,
}) => {
  const [showMore, setShowMore] = useState(false);
  const formationLabel = useSelector(getFormationsLabel(formationId));

  const buildUpStyleDescs = {
    Balanced:
      "Players will vary between forward runs and coming short. The team's transition to their in-possession shape will be steady rather than sudden.",
    Counter:
      "This approach encourages players to get in behind the opposition's defence, as the team transitions quickly from defence to attack.",
    "Short Passing":
      "Players will come short to support the ball carrier rather than make forward runs. This more cautious approach allows the team to maintain their defensive shape for longer during the transition.",
  };

  const defensiveApproachDescs = {
    Deep: "A safety first approach, with the defensive line dropping deeper to mark the opposition.",
    Balanced:
      "This approach gives the defence flexibility on how deep they drop, and which runs they attack.",
    High: "A riskier approach to defending, applying situational pressure, and adopting a high line that tends not to track opposition runs.",
    Aggressive:
      "The most front-footed defensive approach, adopting a high line that focuses on applying press after losing possession, and setting offside traps. Uses more stamina.",
  };

  return (
    <Accordion label="Tactical Preset" defaultOpen={true}>
      <section className="flex flex-col px-4 pt-2 pb-4 text-sm desktop:text-xs gap-y-3">
        <div className="flex flex-col gap-y-2">
          <div className="flex justify-between">
            <p className="text-dark-secondary">Formation:</p>
            <p className="font-semibold">{formationLabel}</p>
          </div>
          {showMore && (
            <div className="mx-auto">
              <TacticFormation formation={formationId} />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <p className="text-dark-secondary">Build-Up Style:</p>
            <p className="font-semibold">{buildUpStyle}</p>
          </div>
          {showMore && (
            <p className="text-dark-tertiary">
              {buildUpStyleDescs[buildUpStyle]}
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <p className="text-dark-secondary whitespace-nowrap">
              Defensive Approach:
            </p>
            <p className="font-semibold">{defensiveApproach}</p>
          </div>
          {showMore && (
            <p className="text-dark-tertiary">
              {defensiveApproachDescs[defensiveApproach]}
            </p>
          )}
        </div>
        <div className="flex justify-between whitespace-nowrap">
          <p className="text-dark-secondary">Line Height:</p>
          <p className="font-semibold">{lineHeight}</p>
        </div>
        <div className="mx-auto">
          <Button
            variant="tertiary"
            label={showMore ? "Show Less" : "Show More"}
            onClick={() => setShowMore((prev) => !prev)}
          />
        </div>
      </section>
    </Accordion>
  );
};

interface TacticChosenSidebarProps {
  tactic: Tactic;
  handleCopy: (eaCode: string) => void;
  isFavorite: boolean;
  handleClickFavorite: () => void;
}
const TacticChosenSidebar = ({
  tactic,
  handleCopy,
  isFavorite,
  handleClickFavorite,
}: TacticChosenSidebarProps) => (
  <section className="flex flex-col w-full gap-y-2">
    <TacticMetaInformationSection
      tactic={tactic}
      handleCopy={handleCopy}
      isFavorite={isFavorite}
      handleClickFavorite={handleClickFavorite}
    />
    <TacticalPresetSection
      formationId={tactic.teamTactics.formationId}
      buildUpStyle={tactic.teamTactics.buildUpStyle}
      defensiveApproach={tactic.teamTactics.defensiveApproach}
      lineHeight={tactic.teamTactics.lineHeight}
    />
    <PlayerRolesSection
      formationId={tactic.teamTactics.formationId}
      playerRoles={tactic.playerRoles}
    />
  </section>
);

export default TacticChosenSidebar;
